import { useCallback, useEffect, useMemo, useState } from 'react';
import { useOutletContext, useParams } from 'react-router-dom';
import { makeStyles } from 'tss-react/mui';

import GppGoodOutlinedIcon from '@mui/icons-material/GppGoodOutlined';
import { Box, Theme, Typography } from '@mui/material';

import {
  getPayoutDetails,
  reInitiateCustomerOnboardingSignal,
} from '../../../api/clientsApi/clientsApi';
import CommonButton from '../../../components/buttons/CommonButton';
import Loader from '../../../components/Loader';
import useSnackBar from '../../../hooks/useSnackBar';
import { useHasPermission } from '../../../hooks/useHasPermission';
import ClientPayoutChangeHistoryContainer from './ClientPayoutChangeHistoryContainer';
import ClientPayoutFormContainer from './ClientPayoutFormContainer';
import SettlementPayoutTables from './SettlementPayoutTables';
import { EntityFlexibleRecord } from '../../../types';
import { toClientFormSource } from './helpers';
import { PayoutDetailsResponse } from './types';

const useStyles = makeStyles()((theme: Theme) => ({
  pageContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
  },
  pageHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    flexWrap: 'wrap',
  },
  pageTitle: {
    color: theme.palette.common.white,
    fontFamily: 'Poppins',
    fontSize: '20px',
    fontWeight: 600,
    lineHeight: '24px',
  },
  fboBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '3px 10px',
    border: `1px solid ${theme.palette.secondary.dark}`,
    borderRadius: '999px',
    backgroundColor: 'rgba(255, 255, 255, 0.06)',
  },
  fboBadgeText: {
    color: theme.palette.text.disabled,
    fontFamily: 'Inter',
    fontSize: '12px',
    fontWeight: 600,
    letterSpacing: '0.5px',
  },
  formWrapper: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '40px',
    width: '100%',
  },
  formContainer: {
    display: 'flex',
    flexDirection: 'column',
    flexBasis: '896px',
    maxWidth: '896px',
    minWidth: 0,
    width: '100%',
    gap: '24px',
  },
  /** Settlement tables need the full page width. */
  formContainerWide: {
    flexBasis: '100%',
    maxWidth: '100%',
  },
  formInnerWrapper: {
    display: 'flex',
    flexDirection: 'column',
    padding: '32px',
    backgroundColor: theme.palette.primary.light,
    borderRadius: '16px',

    '@media (max-width: 800px)': {
      padding: '12px',
    },
  },
  formActionsWrapper: {
    display: 'flex',
    flexDirection: 'column',
    flexBasis: '168px',
    flexShrink: 0,
    width: '168px',
  },
  fieldHeadline: {
    color: theme.palette.common.white,
    fontFamily: 'Inter',
    fontSize: '20px',
    fontWeight: 600,
    lineHeight: '24px',
  },
}));

const ClientPayoutDetailsView = () => {
  const { clientId } = useParams<{ clientId: string }>();
  const { entityData } = useOutletContext<EntityFlexibleRecord>();
  const styles = useStyles();
  const { setAlert } = useSnackBar();
  const canViewPlaintiffOnboarding = useHasPermission('Clients', 'Plaintiff Onboarding');

  const [payoutDetails, setPayoutDetails] = useState<PayoutDetailsResponse | null>(null);
  const [pageLoading, setPageLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);
  /** Settlement being re-initiated; `0` for the client-level (no settlement) action. */
  const [reInitiatingId, setReInitiatingId] = useState<number | null>(null);

  const canEdit = canViewPlaintiffOnboarding && (payoutDetails?.canEdit ?? false);
  const hasSettlements = (payoutDetails?.settlements?.length ?? 0) > 0;
  const canReInitiateGlobal = !hasSettlements && (payoutDetails?.isReinitiateEnabled === true);
  const clientFormSource = useMemo(() => toClientFormSource(payoutDetails), [payoutDetails]);
  const reasonCodes = useMemo(() => payoutDetails?.editReasonCode ?? [], [payoutDetails]);

  const fetchPayoutDetails = useCallback(async (showPageLoader = true) => {
    if (!clientId) return;

    if (showPageLoader) {
      setPageLoading(true);
    }

    try {
      const res = await getPayoutDetails(clientId);
      setPayoutDetails(res.data);
    } catch {
      setAlert((prev) => ({
        ...prev,
        message: 'Failed to load payout details.',
        type: 'error',
      }));
    } finally {
      if (showPageLoader) {
        setPageLoading(false);
      }
    }
  }, [clientId, setAlert]);

  useEffect(() => {
    fetchPayoutDetails();
  }, [fetchPayoutDetails]);

  const handleSaved = () => {
    // Settlement tables refresh in place (no page loader).
    fetchPayoutDetails(!hasSettlements);
    setRefreshKey((prev) => prev + 1);
  };

  const handleReInitiate = async (settlementId?: number) => {
    if (!clientId) return;

    setReInitiatingId(settlementId ?? 0);

    try {
      const res = await reInitiateCustomerOnboardingSignal(Number(clientId), settlementId);
      // Nothing was signalled — the onboarding did not re-run, so don't report success.
      if (res?.data?.fired === 0) {
        throw new Error('Re-initiate did not start. Update the payout details and try again.');
      }
      await fetchPayoutDetails(false);
      if (typeof entityData?.refetchClientRecord === 'function') entityData.refetchClientRecord();
      setAlert((prev) => ({
        ...prev,
        message: 'The Onboarding process has been re-initiated.',
        type: 'success',
      }));
    } catch (err) {
      const message =
        (err as { response?: { data?: { message?: string } } }).response?.data?.message ??
        (err instanceof Error ? err.message : undefined);

      setAlert((prev) => ({
        ...prev,
        message: message || 'Failed to re-initiate onboarding.',
        type: 'error',
      }));
    } finally {
      setReInitiatingId(null);
    }
  };

  return (
    <>
      {pageLoading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
          <Loader colorType="info" size={60} />
        </Box>
      ) : (
        <Box className={styles.classes.pageContainer}>
          <Box className={styles.classes.pageHeader}>
            <GppGoodOutlinedIcon sx={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '22px' }} />
            <Typography className={styles.classes.pageTitle}>Payout Details</Typography>
            <Box className={styles.classes.fboBadge}>
              <Typography className={styles.classes.fboBadgeText}>FBO</Typography>
            </Box>
          </Box>

          <Box className={styles.classes.formWrapper}>
            <Box
              className={styles.cx(
                styles.classes.formContainer,
                hasSettlements && styles.classes.formContainerWide,
              )}
            >
              {/* The client-level account/address is always shown, with or without settlements. */}
              <Box className={styles.classes.formInnerWrapper}>
                <ClientPayoutFormContainer
                  canEdit={canEdit}
                  clientId={clientId ?? ''}
                  onSaved={handleSaved}
                  reasonCodes={reasonCodes}
                  source={clientFormSource}
                />
              </Box>

              {hasSettlements && (
                <SettlementPayoutTables
                  clientId={clientId ?? ''}
                  settlements={payoutDetails?.settlements ?? []}
                  canViewPlaintiffOnboarding={canViewPlaintiffOnboarding}
                  reasonCodes={reasonCodes}
                  onSaved={handleSaved}
                  onReInitiate={handleReInitiate}
                  reInitiatingId={reInitiatingId}
                />
              )}

              <Box className={styles.classes.formInnerWrapper}>
                <ClientPayoutChangeHistoryContainer
                  clientId={clientId ?? ''}
                  refreshKey={refreshKey}
                  settlements={payoutDetails?.settlements ?? undefined}
                />
              </Box>
            </Box>

            {canViewPlaintiffOnboarding && !hasSettlements && (
              <Box className={styles.classes.formActionsWrapper}>
                <Typography className={styles.classes.fieldHeadline}>Actions</Typography>
                <Box mt="16px">
                  <CommonButton
                    buttonText={reInitiatingId !== null ? 'Re-initiating...' : 'Re-initiate'}
                    disabled={reInitiatingId !== null || !canReInitiateGlobal}
                    maxWidth="150px"
                    onButtonClick={() => void handleReInitiate()}
                    type="teal"
                  />
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      )}
    </>
  );
};

export default ClientPayoutDetailsView;
